import { useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Typography, Chip, Select, MenuItem, TextField, IconButton,
  List, ListItem, ListItemIcon, ListItemText, Checkbox,
  Button, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  CheckCircle as MetIcon,
  Warning as WarningIcon,
  Error as GapIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
} from '@mui/icons-material';
import type { GapCriterionView } from '../../../../../services/MedNecService';

const STATUS_CONFIG: Record<string, { color: 'success' | 'warning' | 'error' | 'info'; icon: typeof MetIcon; label: string }> = {
  MET: { color: 'success', icon: MetIcon, label: 'Met' },
  NSTR: { color: 'warning', icon: WarningIcon, label: 'Needs Strengthening' },
  GAP: { color: 'error', icon: GapIcon, label: 'Gap' },
  CGAP: { color: 'error', icon: GapIcon, label: 'Critical Gap' },
};

export const STATUS_OPTIONS = [
  { value: 'MET', label: 'Met' },
  { value: 'NSTR', label: 'Needs Strengthening' },
  { value: 'GAP', label: 'Gap' },
  { value: 'CGAP', label: 'Critical Gap' },
];

interface CriterionItemProps {
  criterion: GapCriterionView;
  depth?: number;
  criterionIndex: number;
  onToggleAction?: (criterionIndex: number, actionIndex: number) => void;
  onStatusChange?: (criterionIndex: number, status: string) => void;
  onCommentSave?: (criterionIndex: number, comment: string | null) => void;
  onRemove?: (criterionIndex: number) => void;
}

const CriterionItem: React.FC<CriterionItemProps> = ({
  criterion, depth = 0, criterionIndex,
  onToggleAction, onStatusChange, onCommentSave, onRemove,
}) => {
  const config = STATUS_CONFIG[criterion.status] || STATUS_CONFIG.GAP;
  const StatusIcon = config.icon;
  const hasComment = !!criterion.nurseComment;
  const [showComment, setShowComment] = useState(hasComment);
  const [commentDraft, setCommentDraft] = useState(criterion.nurseComment ?? '');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleCommentSave = () => {
    const trimmed = commentDraft.trim();
    if (trimmed === (criterion.nurseComment ?? '')) return;
    onCommentSave?.(criterionIndex, trimmed || null);
  };

  const handleRemoveClick = () => {
    if (criterion.isManual) {
      onRemove?.(criterionIndex);
    } else {
      setConfirmOpen(true);
    }
  };

  return (
    <>
      <Accordion
        sx={{
          ml: depth * 2,
          border: '1px solid',
          borderColor: `${config.color}.light`,
          '&::before': { display: 'none' },
          boxShadow: 'none',
        }}
      >
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <StatusIcon color={config.color} fontSize="small" />
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
              {criterion.description}
            </Typography>
            {criterion.isManual && (
              <Chip size="small" label="Manual" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
            )}
            <Select
              size="small"
              value={criterion.status}
              onChange={(e) => { e.stopPropagation(); onStatusChange?.(criterionIndex, e.target.value); }}
              onClick={(e) => e.stopPropagation()}
              sx={{ minWidth: 150, height: 28, fontSize: '0.75rem' }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>{opt.label}</MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary">
              {Math.round(criterion.confidence * 100)}%
            </Typography>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveClick(); }} sx={{ ml: 0.5 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {criterion.policySectionReference && (
              <Typography variant="caption" color="text.secondary">
                Policy: {criterion.policySectionReference}
              </Typography>
            )}
            <Typography variant="body2">{criterion.evaluationReasoning}</Typography>

            {criterion.evidenceQuotes.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Evidence:</Typography>
                {criterion.evidenceQuotes.map((q, i) => (
                  <Typography key={i} variant="body2" sx={{ pl: 1, borderLeft: '2px solid #90caf9', ml: 1, mt: 0.5, fontStyle: 'italic', color: '#546e7a' }}>
                    {q}
                  </Typography>
                ))}
              </Box>
            )}

            {criterion.gapComments && (
              <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                {criterion.gapComments}
              </Alert>
            )}

            {/* Nurse Comment */}
            {showComment ? (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Nurse Comment:</Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={4}
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  onBlur={handleCommentSave}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCommentSave(); } }}
                  placeholder="Add your assessment note..."
                  sx={{ mt: 0.5 }}
                />
              </Box>
            ) : (
              <Button
                size="small"
                startIcon={<CommentIcon />}
                onClick={() => setShowComment(true)}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Comment
              </Button>
            )}

            {criterion.actionItems.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Action Items:</Typography>
                <List dense disablePadding>
                  {criterion.actionItems.map((item, i) => (
                    <ListItem key={i} sx={{ py: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          size="small"
                          checked={item.isChecked}
                          onChange={() => onToggleAction?.(criterionIndex, i)}
                          sx={{ p: 0.25 }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: item.isChecked ? { textDecoration: 'line-through', color: 'text.secondary' } : {},
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {criterion.requiredDocuments.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, mr: 1 }}>Required docs:</Typography>
                {criterion.requiredDocuments.map((doc, i) => (
                  <Chip key={i} label={doc} size="small" variant="outlined" />
                ))}
              </Box>
            )}

            {criterion.subCriteria.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {criterion.subCriteria.map((sub, i) => (
                  <CriterionItem key={i} criterion={sub} depth={depth + 1} criterionIndex={criterionIndex} />
                ))}
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Confirm dialog for removing AI criteria */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Remove Criterion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This criterion was generated by AI. Are you sure you want to remove it?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={() => { setConfirmOpen(false); onRemove?.(criterionIndex); }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CriterionItem;
